<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import CodeField from '@js/components/fields/CodeField.vue';
import EditorField from '@js/components/fields/EditorField.vue';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import FormSeparator from '@js/components/forms/FormSeparator.vue';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { useChangeCase } from '@vueuse/integrations/useChangeCase';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, emailingVariable, emailingVariableTypes } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    emailingVariable: {
        required: false,
        type: Object,
    },
    emailingVariableTypes: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const isSubmitting = ref(false);

const form = reactive({
    name: emailingVariable ? emailingVariable.name : '',
    type: emailingVariable
        ? emailingVariableTypes.find(
              (type) => type.value === emailingVariable.type.value,
          )
        : emailingVariableTypes[0],
    rawValue: emailingVariable ? emailingVariable.rawValue : '',
    isActive: emailingVariable ? emailingVariable.isActive : true,
});

const validationSchema = object({
    name: string().required('Le nom est requis'),
    type: object().required('Le type est requis'),
    rawValue: string().required('La valeur est requise'),
    isActive: boolean().required("L'état actif est requis"),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route('front.projectManagement.modules.postEmailingVariable', {
                tenant: tenant.id,
                ...(emailingVariable
                    ? {
                          emailingVariable: emailingVariable.id,
                      }
                    : {}),
            }),
            {
                name: useChangeCase(form.name, 'snakeCase').value,
                type: form.type.value,
                rawValue: form.rawValue,
                isActive: form.isActive,
            },
        )
        .then(() => {
            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                    tab: 'variables',
                }),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.modules.emailings', {
                    tenant: tenant.id,
                    tabs: 'variables',
                }),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.type"
                        field-id="type"
                        field-name="type"
                        label="Type"
                        :options="emailingVariableTypes"
                        by="value"
                        required
                    >
                        <template #buttonContent>
                            {{ form.type.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.name"
                        label="Nom"
                        field-name="name"
                        field-id="name"
                        required
                    />
                </FormGrid>

                <FormSeparator />

                <SwitchField
                    v-model="form.isActive"
                    label="Est actif"
                    field-name="isActive"
                    field-id="isActive"
                />

                <FormSeparator />

                <CodeField
                    v-if="form.type.value === 'html'"
                    v-model="form.rawValue"
                    field-name="rawValue"
                    field-id="rawValue"
                    label="Valeur"
                    size="small"
                />

                <EditorField
                    v-if="form.type.value === 'editor'"
                    v-model="form.rawValue"
                    label="Valeur"
                    field-name="rawValue"
                    field-id="rawValue"
                    required
                    size="small"
                />

                <InputField
                    v-if="form.type.value === 'string'"
                    v-model="form.rawValue"
                    label="Valeur"
                    field-name="rawValue"
                    field-id="rawValue"
                    type="text"
                    required
                />

                <InputField
                    v-if="form.type.value === 'integer'"
                    v-model="form.rawValue"
                    label="Valeur"
                    field-name="rawValue"
                    field-id="rawValue"
                    type="number"
                    step="1"
                    required
                />

                <InputField
                    v-if="form.type.value === 'float'"
                    v-model="form.rawValue"
                    label="Valeur"
                    field-name="rawValue"
                    field-id="rawValue"
                    type="number"
                    step="0.01"
                    required
                />

                <SwitchField
                    v-if="form.type.value === 'boolean'"
                    v-model="form.rawValue"
                    field-name="rawValue"
                    field-id="rawValue"
                    label="Valeur"
                />

                <InputField
                    v-if="form.type.value === 'date'"
                    v-model="form.rawValue"
                    label="Valeur"
                    field-name="rawValue"
                    field-id="rawValue"
                    type="date"
                    required
                />
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="emailingVariable"
                    :url="
                        route(
                            'front.projectManagement.modules.deleteEmailingVariable',
                            {
                                tenant: tenant.id,
                                emailingVariable: emailingVariable.id,
                            },
                        )
                    "
                    :callback="
                        () => {
                            router.visit(
                                route(
                                    'front.projectManagement.modules.emailings',
                                    {
                                        tenant: tenant.id,
                                        tab: 'variables',
                                    },
                                ),
                            );
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
